import Plot from '@models/Plot';
import Experiment from '@models/Experiment';
import { BaseAnalysisParameters, PrincipalComponentsAnalysisParameters } from '@models/AnalysisParameters';
import { useFormikContext } from 'formik';
import React, { useMemo, useState } from 'react';
import AnalysisVariablesMultiSelectField from '@components/experiments/analyses/assaySummary/AnalysisVariablesMultiSelectField';
import AnalysisGroupsField from '@components/experiments/analyses/assaySummary/AnalysisGroupsField';
import { PrincipalComponentsAnalysisFormValues } from '@components/experiments/analyses/AnalysisFormTypes';
import { ChevronRightIcon } from '@heroicons/react/outline';
import cn from 'classnames';
import { Collapse, Tooltip } from '@mui/material';
import TextInput from '@/src/components/forms/TextInput';
import { HelpCircleIcon } from '@/src/components/icons/custom/HelpCircleIcon';
import Link from 'next/link';

type Props = { plot: Plot; experiment: Experiment; analysisParameters: BaseAnalysisParameters };

const PrincipalComponentsAnalysisFormFields = ({ plot, experiment, analysisParameters }: Props) => {
    const { values, handleChange, ...form } = useFormikContext<PrincipalComponentsAnalysisFormValues>();
    const [advancedExpanded, setAdvancedExpanded] = useState(false);

    const batchAdjustmentVariables = useMemo(
        () => (analysisParameters as PrincipalComponentsAnalysisParameters)?.batch_adjustment_variables ?? [],
        [analysisParameters],
    );

    return (
        <div className="space-y-8">
            <section>
                <p className="mb-2 text-lg font-semibold text-dark">Analyze</p>
                <div className="space-y-2">
                    <label className="block">
                        <input
                            type="radio"
                            className="cursor-pointer text-indigo-500"
                            name="include_all_samples"
                            checked={values.include_all_samples}
                            onChange={() => {
                                form.setFieldValue('include_all_samples', true);
                                form.setFieldValue('variables', []);
                                form.setFieldValue('group_ids', []);
                            }}
                        />
                        <span className="ml-2">Include all samples</span>
                    </label>
                    <label className="block">
                        <input
                            type="radio"
                            className="cursor-pointer text-indigo-500"
                            name="include_all_samples"
                            onChange={() => {
                                form.setFieldValue('include_all_samples', false);
                            }}
                            checked={!values.include_all_samples}
                        />
                        <span className="ml-2">Specific groups of samples</span>
                    </label>
                </div>
                {!values.include_all_samples && (
                    <div className="mt-4">
                        <AnalysisVariablesMultiSelectField
                            variables={(analysisParameters as BaseAnalysisParameters).variables}
                        />
                        <AnalysisGroupsField plot={plot} experiment={experiment} />
                    </div>
                )}
            </section>
            <section>
                <p className="mb-2 text-lg font-semibold text-dark">Data transformations</p>
                <div className="space-y-4">
                    <label className="flex">
                        <input
                            type="checkbox"
                            className="mt-1 cursor-pointer rounded text-indigo-500"
                            name="center_data"
                            checked={values.center_data}
                            onChange={handleChange}
                        />
                        <div className="ml-2 inline-block">
                            <span className="block">
                                <span className="font-semibold">Center data</span> (Recommended)
                            </span>
                            <span>Shifts data to be zero centered</span>
                        </div>
                    </label>
                    <label className="flex">
                        <input
                            type="checkbox"
                            className="mt-1 cursor-pointer rounded text-indigo-500"
                            name="scale_data"
                            checked={values.scale_data}
                            onChange={handleChange}
                        />
                        <div className="ml-2 inline-block">
                            <span className="block">
                                <span className="font-semibold">Scale data</span> (Recommended)
                            </span>
                            <span>Scales data to have unit variance</span>
                        </div>
                    </label>
                </div>
            </section>
            <section>
                <h4
                    className="group mb-4 flex cursor-pointer flex-row items-center justify-between overflow-hidden text-lg font-semibold tracking-tight text-dark"
                    onClick={() => setAdvancedExpanded(!advancedExpanded)}
                >
                    <span>Advanced settings</span>
                    <span>
                        <ChevronRightIcon
                            width={20}
                            className={cn(
                                'cursor-pointer text-primary transition-transform  hover:text-primary/80 group-hover:text-primary/80',
                                { 'rotate-90': advancedExpanded },
                            )}
                        />
                    </span>
                </h4>
                <Collapse in={advancedExpanded}>
                    <div className="flex flex-col gap-4 mb-4">
                        <section className="flex flex-col gap-2">
                            <p className="field-label text-[1rem]">Input data</p>
                            <div className="space-y-2">
                                <label className="block">
                                    <input
                                        type="radio"
                                        className="cursor-pointer text-indigo-500"
                                        name="use_var_features"
                                        checked={!values.use_var_features}
                                        onChange={() => {
                                            form.setFieldValue('use_var_features', false);
                                            form.setFieldValue('num_var_features', null);
                                        }}
                                    />
                                    <span className="ml-2">Use all features</span>
                                </label>
                                <label className="block">
                                    <input
                                        type="radio"
                                        className="cursor-pointer text-indigo-500"
                                        name="use_var_features"
                                        onChange={() => {
                                            form.setFieldValue('use_var_features', true);
                                            form.setFieldValue('num_var_features', 1_000);
                                        }}
                                        checked={values.use_var_features}
                                    />
                                    <span className="ml-2">Subset to top variable features</span>
                                </label>
                            </div>
                            {values.use_var_features && (
                                <div className="mt-2">
                                    <TextInput
                                        name="num_var_features"
                                        label="Most variable features to include"
                                        type="number"
                                        value={values.num_var_features ?? ''}
                                        onValueChange={(_, __, values) =>
                                            form.setFieldValue('num_var_features', values?.float ?? 0)
                                        }
                                        fixedDecimalLength={0}
                                        allowNegativeValue={true}
                                        step={1}
                                        useNumericFormat
                                        min={100}
                                        max={10_000}
                                    />
                                </div>
                            )}
                        </section>
                        <section className="flex flex-col gap-2">
                            <div className="flex flex-row items-center gap-2">
                                <p className="field-label text-[1rem] !w-auto">Batch correction</p>
                                <Tooltip
                                    title={
                                        'Batch correction will only apply to this PCA visualization. It will not affect other analyses. Learn more.'
                                    }
                                    placement="right"
                                    arrow
                                >
                                    <Link
                                        href="https://help.pluto.bio/en/articles/6013438-principal-components-analysis-pca"
                                        target="_blank"
                                    >
                                        <HelpCircleIcon height={16} width={16} color="#3343AB" />
                                    </Link>
                                </Tooltip>
                            </div>
                            <div className="space-y-2">
                                <label className="block">
                                    <input
                                        type="radio"
                                        className="cursor-pointer text-indigo-500"
                                        name="batch_adjustment_method"
                                        checked={values.batch_adjustment_method === 'none'}
                                        onChange={() => {
                                            form.setFieldValue('batch_adjustment_method', 'none');
                                            form.setFieldValue('batch_adjustment_var', null);
                                        }}
                                    />
                                    <span className="ml-2">No batch correction</span>
                                </label>
                                <label className="block">
                                    <input
                                        type="radio"
                                        className="cursor-pointer text-indigo-500"
                                        name="batch_adjustment_method"
                                        onChange={() => {
                                            form.setFieldValue('batch_adjustment_method', 'limma');
                                        }}
                                        checked={values.batch_adjustment_method === 'limma'}
                                    />
                                    <span className="ml-2">Batch correction (limma::removeBatchEffect)</span>
                                </label>
                            </div>
                            {values.batch_adjustment_method === 'limma' && (
                                <div className="mt-2">
                                    <AnalysisVariablesMultiSelectField
                                        variables={batchAdjustmentVariables.map((variable, key) => ({
                                            id: key,
                                            display_name: variable,
                                        }))}
                                        label="Variable to correct by"
                                        maxItems={1}
                                        variablesName="batch_adjustment_var"
                                        useVariableName
                                        disableGroups
                                        useSingleValue
                                    />
                                </div>
                            )}
                        </section>
                    </div>
                </Collapse>
            </section>
        </div>
    );
};

export default PrincipalComponentsAnalysisFormFields;
